import { addMonths, endOfMonth, format, isEqual, subMonths } from 'date-fns';
import { Dispatch, useMemo, useState } from 'react';
import styled from 'styled-components';
import { LoadingSpinner } from '../../common/components/LoadingSpinner/LoadingSpinner';
import { ThemeColours } from '../../common/styles/ThemeColours';
import { FontWeight, FONT_STACK } from '../../common/styles/constants';
import { onMobile } from '../../common/styles/onMobile';
import { lastOf } from '../../common/util/arrays/lastOf';
import { now } from '../../common/util/now';
import { run } from '../../common/util/run';
import { CalendarChevron } from './CalendarChevron/CalendarChevron';
import { CalendarGrid } from './CalendarGrid/CalendarGrid';

interface Props {
  className?: string;
  currentDate?: Date;
  disableWhitelist?: boolean;
  whitelistDates?: Date[];
  loading?: boolean;
  onChange?: Dispatch<Date>;
  value?: Date;
  endValue?: Date;
}

const DAY_ABBREVIATIONS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const CalendarView = ({
  className = '',
  currentDate = now(),
  disableWhitelist = false,
  whitelistDates = [],
  loading = false,
  onChange,
  value,
  endValue,
}: Props) => {
  const days = useMemo(
    () => (
      <Days>
        {DAY_ABBREVIATIONS.map(day => (
          <Day key={day}>{day}</Day>
        ))}
      </Days>
    ),
    [],
  );

  const [month, setMonth] = useState(value || currentDate);

  const previousMonth = () => setMonth(subMonths(month, 1));

  const nextMonth = () => setMonth(addMonths(month, 1));

  const noDates = whitelistDates.length === 0;
  const lastDate = noDates ? currentDate : lastOf(whitelistDates);

  const firstMonth = disableWhitelist ? false : isEqual(endOfMonth(currentDate), endOfMonth(month));

  const finalMonth = run(() => {
    if (disableWhitelist) return false;
    if (noDates) return true;
    if (!lastDate) return false;
    return endOfMonth(lastDate) < endOfMonth(month);
  });

  const monthDates = whitelistDates.filter(date => isEqual(endOfMonth(date), endOfMonth(month)));

  return (
    <Outer className={className}>
      <Header>
        <CalendarChevron
          data-testid="Previous month"
          $disabled={firstMonth}
          onClick={previousMonth}
        />

        <Title title="Month">{format(month, 'MMMM yyyy')}</Title>

        <CalendarChevron
          data-testid="Next month"
          $disabled={finalMonth}
          $right={true}
          onClick={nextMonth}
        />
      </Header>

      {days}

      {loading && <StyledSpinner />}

      {finalMonth && !disableWhitelist && !loading && (
        <Message data-testid="Message">
          No {!noDates && 'further '}appointments have been published yet.
        </Message>
      )}

      {(!finalMonth || disableWhitelist) && !loading && lastDate && (
        <CalendarGrid
          currentDate={currentDate}
          disableWhitelist={disableWhitelist}
          whitelistDates={monthDates}
          value={value}
          endValue={endValue}
          onChange={onChange}
          lastDate={lastDate}
          month={month}
        />
      )}
    </Outer>
  );
};

const StyledSpinner = styled(LoadingSpinner)`
  height: 64px;
  margin-top: 64px;
`;

const Message = styled.div`
  font-size: 16px;
  line-height: 1.44em;
  margin: 48px auto;
  max-width: 192px;
  text-align: center;
`;

const Day = styled.div`
  text-align: center;
  width: 28px;
`;

const Days = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 14px;
  font-weight: ${FontWeight.BOLD};
  justify-content: space-around;
  margin: 16px 0;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: ${FontWeight.BOLD};
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Outer = styled.div`
  border-radius: 4px;
  color: ${ThemeColours.black};
  ${FONT_STACK};
  height: 224px;
  padding: 0 32px;
  user-select: none;
  ${onMobile} {
    height: 272px;
    padding: 0 24px;
  }
`;
